import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import styled from 'styled-components';
import gql from 'graphql-tag';
import BookingCalendar from './components/BookingCalendar';
import BookingTimeSelection from './components/BookingTimeSelection';
import { themes, AppointmentType, TimeSlot, ReduxState } from 'kb-shared';
import moment, { Moment as TMoment } from 'moment';
import Loader from '../../../../components/Loader';
import { AdditionalInfoPanel } from '../AdditionalInfoPanel';
import { mappedTimeZone, MOMENT_TIME_ZONES } from 'kb-shared/utilities/momentHelpers';
import { useSelector } from 'react-redux';

interface TimeSlotsResults {
  dateTimeSlots: Array<TimeSlot>;
  patient: {
    lab: {
      timeZone: string;
    };
  };
}

const dateTimeSlotsQuery = gql`
  query dateTimeSlots(
    $appointmentTypeId: Int!
    $locationId: Int
    $startDateTime: String!
    $endDateOffsetInMonths: Int
    $labId: Int
  ) {
    dateTimeSlots(
      appointmentTypeId: $appointmentTypeId
      locationId: $locationId
      startDateTime: $startDateTime
      endDateOffsetInMonths: $endDateOffsetInMonths
      labId: $labId
    ) {
      id
      startTime
      endTime
      timeZone
      lab {
        id
        stripePublicKey
      }
    }
  }
`;

const BookingCalendarTimeContainer = styled.div`
  @media screen and (max-width: ${themes.breakpoints.tabletPortrait}) {
    flex-direction: column;
  }
  display: flex;
  flex-grow: 1;
  padding: 5px;
`;

interface Props {
  appointmentType: AppointmentType;
  labId: number | null;
  locationId: number | null;
  selectedTimeSlot: TimeSlot | null;
  onSelectTimeSlot: (slot: TimeSlot | null) => void;
}

const BookingCalendarTime = (props: Props) => {
  const [selectedDate, setSelectedDate] = useState<TMoment | undefined>(undefined);
  const { patient } = useSelector((state: ReduxState) => state.patient);
  const preferredTimezone = patient.preferredTimezone as keyof typeof MOMENT_TIME_ZONES;
  const labTimezone = patient.lab?.timeZone;
  const [currentMonth, setCurrentMonth] = useState(
    moment.tz(mappedTimeZone(preferredTimezone || labTimezone || 'UTC'))
  );
  const [appointmentFound, setAppointmentFound] = useState<boolean>(false);
  const today = moment();

  const adjustSelectedDate = (selectedDate: TMoment) => {
    setSelectedDate(selectedDate);
    props.onSelectTimeSlot(null);
  };

  const adjustCurrentMonth = (adjustMonth: number) => {
    setSelectedDate(undefined);
    setCurrentMonth(currentMonth.clone().add(adjustMonth, 'months'));
    props.onSelectTimeSlot(null);
  };

  const cleanResults = (data: Array<TimeSlot>) => {
    return data.reduce((allSlots: Array<TimeSlot>, currentTimeSlot: TimeSlot) => {
      const utcStartTime = moment(currentTimeSlot.startTime).utc();

      const hasTimeSlot = allSlots.find(timeSlot => utcStartTime.isSame(timeSlot.utcStartTime));

      if (!hasTimeSlot) {
        allSlots.push({
          ...currentTimeSlot,
          utcStartTime
        });
      }
      return allSlots;
    }, []);
  };

  const findSelectedTimeSlots = (allSlots: Array<TimeSlot>) => {
    const selectedAvailableTimeSlots = allSlots.filter(timeSlot => {
      return selectedDate?.date() == moment.parseZone(timeSlot.startTime).date();
    });
    return selectedAvailableTimeSlots;
  };

  const locationId =
    typeof props.locationId == 'string' ? parseInt(props.locationId) : props.locationId;

  const isVirtual = props.appointmentType.virtual;
  const appointmentTypeId = parseInt(props.appointmentType.id);

  const { data, loading } = useQuery<TimeSlotsResults>(dateTimeSlotsQuery, {
    variables: {
      appointmentTypeId: appointmentTypeId,
      locationId: locationId ? locationId : null,
      startDateTime: currentMonth?.clone().startOf('month') || moment().startOf('month'),
      endDateOffsetInMonths: 1,
      labId: props.labId
    }
  });

  useEffect(() => {
    if (!data || !data.dateTimeSlots || loading || selectedDate) return;
    const dateTimeSlots = cleanResults(data.dateTimeSlots);
    let selectedStartDate =
      dateTimeSlots.length > 0
        ? moment(dateTimeSlots[0].startTime).startOf('day')
        : currentMonth.clone().startOf('month');

    if (selectedStartDate?.date() == today.date() && selectedStartDate?.month() == today.month()) {
      selectedStartDate = selectedStartDate.clone().add(1, 'days');
    }

    setSelectedDate(selectedStartDate);

    if (!appointmentFound && !dateTimeSlots?.length) {
      adjustCurrentMonth(1);
      return;
    }
    setAppointmentFound(true);
  }, [data, currentMonth, loading, selectedDate, adjustCurrentMonth, appointmentFound, today]);

  if (!data || !data.dateTimeSlots)
    return <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />;

  const dateTimeSlots = cleanResults(data.dateTimeSlots);
  const availableTimeSlots = findSelectedTimeSlots(dateTimeSlots);

  return (
    <>
      <BookingCalendarTimeContainer>
        {loading || !appointmentFound ? (
          <Loader type="spin" color={themes.colors.KBYellow} height={150} width={150} />
        ) : (
          <>
            <BookingCalendar
              // @ts-ignore
              currentMonth={currentMonth}
              // @ts-ignore
              selectedDate={selectedDate}
              timeSlotsResults={dateTimeSlots}
              onPrevious={() => {
                adjustCurrentMonth(-1);
              }}
              onNext={() => {
                adjustCurrentMonth(1);
              }}
              onSelectDate={(newDate: TMoment) => {
                adjustSelectedDate(newDate);
              }}
              renderBorder={true}
            />
            <BookingTimeSelection
              selectedTimeSlot={props.selectedTimeSlot}
              onSelectTimeSlot={props.onSelectTimeSlot}
              timeslots={availableTimeSlots}
              date={selectedDate}
            />
          </>
        )}
      </BookingCalendarTimeContainer>
      {isVirtual ? (
        <AdditionalInfoPanel>
          For services that require you to be seen in clinic, such as fertility care, the provider
          you meet with virtually may differ from your treating provider.
          <p>
            In order to provide you with the earliest availability for your appointment, the
            appointment times listed include all providers for this location.
          </p>
        </AdditionalInfoPanel>
      ) : (
        <AdditionalInfoPanel>
          If you don’t find an available time slot that works for you, you can try booking a virtual
          appointment with us instead.
        </AdditionalInfoPanel>
      )}
    </>
  );
};

export default BookingCalendarTime;
